import Spell.Companion.SpellView
import androidx.compose.runtime.Composable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.renderComposable


@JsModule("./spells.json")
@JsNonModule
external val spellsJson: Json
val format = Json { ignoreUnknownKeys = true }

val allSpells = format.decodeFromString<List<Spell>>(JSON.stringify(spellsJson)).sortedWith(
    compareBy({ it.level }, { it.name })
)

fun <TraitType : Comparable<TraitType>> getAllFromSpells(gatherFunction: (Spell) -> Set<TraitType>) =
    allSpells.map(gatherFunction).reduceRight { a, b -> a.union(b) }.toList().sorted()

fun <TraitType : Comparable<TraitType>> getAllFromSpellsMono(gatherFunction: (Spell) -> TraitType) =
    allSpells.map(gatherFunction).map { setOf(it) }.reduceRight { a, b -> a.union(b) }.toList().sorted()

fun <TraitType : Comparable<TraitType>> getAllFromSpellsMonoNullable(gatherFunction: (Spell) -> TraitType?) = allSpells
    .map(gatherFunction)
    .map { setOf(it) }
    .reduceRight { a, b -> a.union(b) }.toList()
    .sortedWith { a, b ->
        if (a == b) {
            0
        } else if (a == null) {
            -1
        } else if (b == null) {
            1
        } else {
            a.compareTo(b)
        }
    }

fun <T> List<T>.withFavourites(favourites: List<T>): List<T> {
    val allAsSet = this.toSet()
    val allAsMutable = this.toMutableList()
    allAsMutable.removeAll(favourites)
    val result = favourites.filter { allAsSet.contains(it) }.toMutableList()
    result.addAll(allAsMutable)
    return result
}

val renderStringAsText = @Composable { value: String ->
    if (value.isBlank())
        I { Text("none") }
    else
        Text(value)
}
val renderIntAsText = @Composable { value: Int? ->
    if (value == null)
        I { Text("none") }
    else
        Text(value.toString())
}
val renderArea = @Composable { value: Int? ->
    if (value == null)
        I { Text("none") }
    else
        Text("$value-feet")
}
val filterTextWithNone: (String, String) -> Boolean = { searchText, item ->
    if (searchText.isBlank()) {
        true
    } else if (item.isBlank()) {
        "none".contains(searchText)
    } else {
        item.contains(searchText)
    }
}

val traitsFilter =
    IncludeExcludeFilter<Spell>(
        "Trait",
        getAllFromSpells { it.traits.toSet() },
        filterTextWithNone
    ) { dataItem, required, excluded ->
        dataItem.traits.containsAll(required) && dataItem.traits.intersect(excluded).isEmpty()
    }
val traditionsFilter = IncludeExcludeFilter<Spell>(
    "Tradition",
    listOf("arcane", "primal", "occult", "divine"),
    null
) { dataItem, required, excluded ->
    dataItem.traditions.containsAll(required) && dataItem.traditions.intersect(excluded).isEmpty()
}
val componentsFilter =
    IncludeExcludeFilter<Spell>(
        "Components",
        listOf("verbal", "somatic", "material"),
        null
    ) { dataItem, required, excluded ->
        dataItem.components.toSet().containsAll(required) && dataItem.components.toSet().intersect(excluded).isEmpty()
    }

val schoolFilter = CheckboxFilterSpec<Spell, String>(
    "School",
    getAllFromSpellsMono { it.school },
    null,
    renderStringAsText
) { spell, allowedSchools -> allowedSchools.contains(spell.school) }

val durationFilter = CheckboxFilterSpec<Spell, String>(
    "Duration",
    getAllFromSpellsMono { it.duration },
    filterTextWithNone,
    renderStringAsText
) { spell, allowedDurations -> allowedDurations.contains(spell.duration) }

val castTimeFilter = CheckboxFilterSpec<Spell, String>(
    "Cast time",
    getAllFromSpellsMono { it.castTime }
        .withFavourites(
            listOf(
                "1", "2", "3", "reaction", "free", "1 or 2", "1 to 3", "2 or 3", "2 to 2 rounds",
                "1 minute", "5 minutes", "10 minutes", "30 minutes", "1 hour", "1 day"
            )
        ),
    null,
    { CastTime(it) }
) { spell, allowedCastTimes -> allowedCastTimes.contains(spell.castTime) }

val categoryFilter = CheckboxFilterSpec<Spell, String>(
    "Category",
    getAllFromSpellsMono { it.category },
    null,
    renderStringAsText
) { spell, allowedCategories -> allowedCategories.contains(spell.category) }

val levelFilter = CheckboxFilterSpec<Spell, Int>(
    "Level",
    getAllFromSpellsMono { it.level },
    null,
    renderIntAsText
) { spell, allowedLevels -> allowedLevels.contains(spell.level) }

val targetFilter = CheckboxFilterSpec<Spell, String>(
    "Target",
    getAllFromSpellsMono { it.target },
    filterTextWithNone,
    renderStringAsText
) { spell, allowedTargets -> allowedTargets.contains(spell.target) }

val areaSizeFilter = CheckboxFilterSpec<Spell, Int?>(
    "Area size",
    getAllFromSpellsMonoNullable { it.areaSize },
    null,
    renderArea
) { spell, allowedAreaSizes -> allowedAreaSizes.contains(spell.areaSize) }

val areaTypeFilter = CheckboxFilterSpec<Spell, String>(
    "Area type",
    getAllFromSpellsMono { it.areaType },
    null,
    renderStringAsText
) { spell, allowedRanges -> allowedRanges.contains(spell.areaType) }

val rangeFavourites = listOf(
    "",
    "self",
    "touch",
    "0 feet",
    "5 feet",
    "touch or 30 ft"
)
val rangeFilter = CheckboxFilterSpec<Spell, String>(
    "Range",
    getAllFromSpellsMono { it.range }.withFavourites(rangeFavourites),
    filterTextWithNone,
    renderStringAsText
) { spell, allowedRanges -> allowedRanges.contains(spell.range) }

val sourceFavourites = listOf(
    "Pathfinder Core Rulebook",
    "Pathfinder Advanced Player's Guide",
    "Pathfinder Secrets of Magic",
    "Pathfinder Lost Omens: Gods & Magic",
    "Pathfinder Lost Omens: Character Guide",
    "Pathfinder Lost Omens: Legends",
    "Pathfinder Lost Omens: World Guide",
    "Pathfinder Lost Omens: Pathfinder Society Guide",
    "Pathfinder Bestiary",
    "Pathfinder Bestiary 2",
    "Pathfinder Bestiary 3",
)

val sourceFilter = CheckboxFilterSpec<Spell, String>(
    "Source",
    getAllFromSpellsMono { it.source }.withFavourites(sourceFavourites),
    filterTextWithNone,
    renderStringAsText
) { spell, allowedSources -> allowedSources.contains(spell.source) }

val nameFilter =
    TextFilterSpec<Spell>("Name") { spell, filterText ->
        filterText.isBlank() || spell.name.contains(
            filterText,
            ignoreCase = true
        )
    }
val descriptionFilter = TextFilterSpec<Spell>(
    "Description"
) { spell, filterText -> filterText.isBlank() || spell.description.contains(filterText, ignoreCase = true) }

val filters = listOf(
    traitsFilter,
    traditionsFilter,
    componentsFilter,
    schoolFilter,
    durationFilter,
    castTimeFilter,
    categoryFilter,
    levelFilter,
    targetFilter,
    areaSizeFilter,
    areaTypeFilter,
    rangeFilter,
    sourceFilter,
    nameFilter,
    descriptionFilter,
)

fun main() {
    renderComposable(rootElementId = "root") {
        Div({ style { margin(0.px); backgroundColor(Color("#F3F3FF")); fontFamily("helvetica") } }) {
            FilterableList(filters, allSpells) { SpellView(it) }
        }
    }
}